'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useMarketData } from '@/contexts/MarketDataContext';
import { Bell, BellOff, Plus, X, Trash2, CheckCircle2, AlertTriangle, Activity, ChevronDown, ChevronUp, Zap, Target, RefreshCw,  } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type AlertCategory = 'PRICE' | 'TECHNICAL';
type PriceCondition = 'ABOVE' | 'BELOW' | 'CROSSES_UP' | 'CROSSES_DOWN';
type TriggerType = 'EMA_CROSS' | 'RSI_BREACH' | 'MACD_CROSS' | 'VWAP_CROSS' | 'BOLLINGER_BREAK';
type RsiDirection = 'ABOVE' | 'BELOW';

interface TradingAlert {
  id: string;
  userId: string;
  ticker: string;
  companyName: string;
  alertCategory: AlertCategory;
  condition: PriceCondition;
  targetPrice: number | null;
  triggerType: TriggerType | null;
  emaFast: number | null;
  emaSlow: number | null;
  rsiPeriod: number | null;
  rsiLevel: number | null;
  rsiDirection: RsiDirection | null;
  isActive: boolean;
  isTriggered: boolean;
  triggeredAt: string | null;
  triggeredPrice: number | null;
  notifyInApp: boolean;
  notes: string;
  createdAt: string;
}

interface AlertFormData {
  ticker: string;
  companyName: string;
  alertCategory: AlertCategory;
  condition: PriceCondition;
  targetPrice: string;
  triggerType: TriggerType;
  emaFast: string;
  emaSlow: string;
  rsiPeriod: string;
  rsiLevel: string;
  rsiDirection: RsiDirection;
  notes: string;
}

interface ToastNotification {
  id: string;
  alertId: string;
  ticker: string;
  message: string;
  price: number;
  timestamp: number;
}

// ─── EMA / RSI helpers (client-side approximation on tick history) ─────────────

// Simple EMA from an array of closes
function calcEMA(closes: number[], period: number): number[] {
  if (closes.length < period) return [];
  const k = 2 / (period + 1);
  const result: number[] = [];
  let ema = closes.slice(0, period).reduce((a, b) => a + b, 0) / period;
  result.push(ema);
  for (let i = period; i < closes.length; i++) {
    ema = closes[i] * k + ema * (1 - k);
    result.push(ema);
  }
  return result;
}

// RSI from closes array
function calcRSI(closes: number[], period: number): number {
  if (closes.length < period + 1) return 50;
  let gains = 0, losses = 0;
  for (let i = closes.length - period; i < closes.length; i++) {
    const diff = closes[i] - closes[i - 1];
    if (diff > 0) gains += diff;
    else losses += Math.abs(diff);
  }
  const avgGain = gains / period;
  const avgLoss = losses / period;
  if (avgLoss === 0) return 100;
  const rs = avgGain / avgLoss;
  return 100 - 100 / (1 + rs);
}

// ─── Constants ────────────────────────────────────────────────────────────────

const KNOWN_STOCKS: Record<string, string> = {
  RELIANCE: 'Reliance Industries', TCS: 'Tata Consultancy Services',
  HDFCBANK: 'HDFC Bank', INFY: 'Infosys', ICICIBANK: 'ICICI Bank',
  BAJFINANCE: 'Bajaj Finance', WIPRO: 'Wipro', AXISBANK: 'Axis Bank',
  SUNPHARMA: 'Sun Pharmaceutical', MARUTI: 'Maruti Suzuki',
  TATASTEEL: 'Tata Steel', ONGC: 'ONGC',
};

const TRIGGER_LABELS: Record<TriggerType, string> = {
  EMA_CROSS: 'EMA Cross', RSI_BREACH: 'RSI Breach',
  MACD_CROSS: 'MACD Cross', VWAP_CROSS: 'VWAP Cross',
  BOLLINGER_BREAK: 'Bollinger Break',
};

const defaultForm: AlertFormData = {
  ticker: '', companyName: '', alertCategory: 'PRICE',
  condition: 'ABOVE', targetPrice: '',
  triggerType: 'EMA_CROSS', emaFast: '9', emaSlow: '21',
  rsiPeriod: '14', rsiLevel: '30', rsiDirection: 'BELOW',
  notes: '',
};

// ─── Row mapper ───────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToAlert(r: any): TradingAlert {
  return {
    id: r.id, userId: r.user_id, ticker: r.ticker,
    companyName: r.company_name, alertCategory: r.alert_category,
    condition: r.condition, targetPrice: r.target_price ? Number(r.target_price) : null,
    triggerType: r.trigger_type, emaFast: r.ema_fast, emaSlow: r.ema_slow,
    rsiPeriod: r.rsi_period, rsiLevel: r.rsi_level ? Number(r.rsi_level) : null,
    rsiDirection: r.rsi_direction, isActive: r.is_active, isTriggered: r.is_triggered,
    triggeredAt: r.triggered_at, triggeredPrice: r.triggered_price ? Number(r.triggered_price) : null,
    notifyInApp: r.notify_in_app, notes: r.notes || '', createdAt: r.created_at,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AlertsContent() {
  const { user } = useAuth();
  const supabase = createClient();
  const { ticks } = useMarketData();

  const [alerts, setAlerts] = useState<TradingAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<AlertFormData>(defaultForm);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'active' | 'triggered' | 'all'>('active');
  const [toasts, setToasts] = useState<ToastNotification[]>([]);

  // Per-symbol price history for technical indicator computation
  const priceHistoryRef = useRef<Record<string, number[]>>({});
  // Track previous EMA cross state to detect crossovers
  const prevEmaStateRef = useRef<Record<string, 'fast_above' | 'fast_below' | null>>({});
  // Alerts already fired this session (avoid re-firing on same tick)
  const firedAlertsRef = useRef<Set<string>>(new Set());

  // ── Load alerts ─────────────────────────────────────────────────────────────
  const loadAlerts = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('trading_alerts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (err) throw err;
      setAlerts((data || []).map(rowToAlert));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  // ── Supabase realtime subscription ──────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('trading_alerts_changes')
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'trading_alerts',
        filter: `user_id=eq.${user.id}`,
      }, () => { loadAlerts(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, supabase, loadAlerts]);

  // ── Toast helpers ────────────────────────────────────────────────────────────
  const addToast = useCallback((alertId: string, ticker: string, message: string, price: number) => {
    const toast: ToastNotification = {
      id: `${alertId}-${Date.now()}`, alertId, ticker, message, price, timestamp: Date.now(),
    };
    setToasts(prev => [toast, ...prev].slice(0, 5));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== toast.id));
    }, 8000);
  }, []);

  // ── Mark alert as triggered in DB ───────────────────────────────────────────
  const markTriggered = useCallback(async (alertId: string, price: number) => {
    await supabase.from('trading_alerts').update({
      is_triggered: true, is_active: false,
      triggered_at: new Date().toISOString(), triggered_price: price,
    }).eq('id', alertId);
  }, [supabase]);

  // ── Evaluate alerts on every tick ───────────────────────────────────────────
  useEffect(() => {
    if (!alerts.length) return;
    const activeAlerts = alerts.filter(a => a.isActive && !a.isTriggered);
    if (!activeAlerts.length) return;

    for (const alert of activeAlerts) {
      const tick = ticks[alert.ticker];
      if (!tick) continue;
      const ltp = tick.ltp;

      // Accumulate price history (max 200 candles)
      const hist = priceHistoryRef.current[alert.ticker] || [];
      if (!hist.length || hist[hist.length - 1] !== ltp) {
        hist.push(ltp);
        if (hist.length > 200) hist.shift();
        priceHistoryRef.current[alert.ticker] = hist;
      }

      if (firedAlertsRef.current.has(alert.id)) continue;

      let fired = false;
      let message = '';

      if (alert.alertCategory === 'PRICE' && alert.targetPrice !== null) {
        const prev = hist.length >= 2 ? hist[hist.length - 2] : ltp;
        switch (alert.condition) {
          case 'ABOVE':
            if (ltp >= alert.targetPrice) { fired = true; message = `${alert.ticker} crossed ABOVE ₹${alert.targetPrice}`; }
            break;
          case 'BELOW':
            if (ltp <= alert.targetPrice) { fired = true; message = `${alert.ticker} dropped BELOW ₹${alert.targetPrice}`; }
            break;
          case 'CROSSES_UP':
            if (prev < alert.targetPrice && ltp >= alert.targetPrice) { fired = true; message = `${alert.ticker} crossed UP through ₹${alert.targetPrice}`; }
            break;
          case 'CROSSES_DOWN':
            if (prev > alert.targetPrice && ltp <= alert.targetPrice) { fired = true; message = `${alert.ticker} crossed DOWN through ₹${alert.targetPrice}`; }
            break;
        }
      } else if (alert.alertCategory === 'TECHNICAL') {
        switch (alert.triggerType) {
          case 'EMA_CROSS': {
            const fast = alert.emaFast || 9;
            const slow = alert.emaSlow || 21;
            if (hist.length >= slow + 5) {
              const emaFastArr = calcEMA(hist, fast);
              const emaSlowArr = calcEMA(hist, slow);
              if (emaFastArr.length >= 2 && emaSlowArr.length >= 2) {
                const fastNow = emaFastArr[emaFastArr.length - 1];
                const slowNow = emaSlowArr[emaSlowArr.length - 1];
                const fastPrev = emaFastArr[emaFastArr.length - 2];
                const slowPrev = emaSlowArr[emaSlowArr.length - 2];
                const stateKey = alert.id;
                const prevState = prevEmaStateRef.current[stateKey];
                const curState: 'fast_above' | 'fast_below' = fastNow > slowNow ? 'fast_above' : 'fast_below';
                if (prevState && prevState !== curState) {
                  fired = true;
                  message = curState === 'fast_above'
                    ? `${alert.ticker} EMA${fast} crossed ABOVE EMA${slow} (Bullish)`
                    : `${alert.ticker} EMA${fast} crossed BELOW EMA${slow} (Bearish)`;
                }
                prevEmaStateRef.current[stateKey] = curState;
                // suppress unused warning
                void fastPrev; void slowPrev;
              }
            }
            break;
          }
          case 'RSI_BREACH': {
            const period = alert.rsiPeriod || 14;
            const level = alert.rsiLevel || 30;
            const dir = alert.rsiDirection || 'BELOW';
            if (hist.length >= period + 2) {
              const rsi = calcRSI(hist, period);
              const prevRsi = calcRSI(hist.slice(0, -1), period);
              if (dir === 'BELOW' && prevRsi >= level && rsi < level) {
                fired = true; message = `${alert.ticker} RSI(${period}) breached BELOW ${level} (Oversold)`;
              } else if (dir === 'ABOVE' && prevRsi <= level && rsi > level) {
                fired = true; message = `${alert.ticker} RSI(${period}) breached ABOVE ${level} (Overbought)`;
              }
            }
            break;
          }
          case 'VWAP_CROSS': {
            if (tick.atp && hist.length >= 2) {
              const vwap = tick.atp;
              const prev = hist[hist.length - 2];
              if (prev < vwap && ltp >= vwap) { fired = true; message = `${alert.ticker} crossed ABOVE VWAP (₹${vwap.toFixed(2)})`; }
              else if (prev > vwap && ltp <= vwap) { fired = true; message = `${alert.ticker} crossed BELOW VWAP (₹${vwap.toFixed(2)})`; }
            }
            break;
          }
          default:
            break;
        }
      }

      if (fired) {
        firedAlertsRef.current.add(alert.id);
        if (alert.notifyInApp) addToast(alert.id, alert.ticker, message, ltp);
        markTriggered(alert.id, ltp);
        setAlerts(prev => prev.map(a => a.id === alert.id
          ? { ...a, isTriggered: true, isActive: false, triggeredAt: new Date().toISOString(), triggeredPrice: ltp }
          : a
        ));
      }
    }
  }, [ticks, alerts, addToast, markTriggered]);

  // ── Save alert ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!user) return;
    if (!form.ticker.trim()) { setError('Ticker is required'); return; }
    if (form.alertCategory === 'PRICE' && !form.targetPrice) { setError('Target price is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        user_id: user.id,
        ticker: form.ticker.toUpperCase().trim(),
        company_name: form.companyName || KNOWN_STOCKS[form.ticker.toUpperCase().trim()] || form.ticker.toUpperCase().trim(),
        alert_category: form.alertCategory,
        condition: form.condition,
        target_price: form.alertCategory === 'PRICE' ? parseFloat(form.targetPrice) : null,
        trigger_type: form.alertCategory === 'TECHNICAL' ? form.triggerType : null,
        ema_fast: form.alertCategory === 'TECHNICAL' && form.triggerType === 'EMA_CROSS' ? parseInt(form.emaFast) : null,
        ema_slow: form.alertCategory === 'TECHNICAL' && form.triggerType === 'EMA_CROSS' ? parseInt(form.emaSlow) : null,
        rsi_period: form.alertCategory === 'TECHNICAL' && form.triggerType === 'RSI_BREACH' ? parseInt(form.rsiPeriod) : null,
        rsi_level: form.alertCategory === 'TECHNICAL' && form.triggerType === 'RSI_BREACH' ? parseFloat(form.rsiLevel) : null,
        rsi_direction: form.alertCategory === 'TECHNICAL' && form.triggerType === 'RSI_BREACH' ? form.rsiDirection : null,
        is_active: true, is_triggered: false,
        notify_in_app: true,
        notes: form.notes,
      };
      const { error: err } = await supabase.from('trading_alerts').insert(payload);
      if (err) throw err;
      setForm(defaultForm);
      setShowForm(false);
      await loadAlerts();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save alert');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete alert ─────────────────────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    await supabase.from('trading_alerts').delete().eq('id', id);
    setAlerts(prev => prev.filter(a => a.id !== id));
  };

  // ── Toggle active ────────────────────────────────────────────────────────────
  const handleToggle = async (alert: TradingAlert) => {
    const newActive = !alert.isActive;
    await supabase.from('trading_alerts').update({ is_active: newActive }).eq('id', alert.id);
    setAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, isActive: newActive } : a));
    if (newActive) firedAlertsRef.current.delete(alert.id);
  };

  // ── Filtered lists ───────────────────────────────────────────────────────────
  const filteredAlerts = alerts.filter(a => {
    if (activeTab === 'active') return a.isActive && !a.isTriggered;
    if (activeTab === 'triggered') return a.isTriggered;
    return true;
  });

  const activeCount = alerts.filter(a => a.isActive && !a.isTriggered).length;
  const triggeredCount = alerts.filter(a => a.isTriggered).length;

  // ── Render helpers ────────────────────────────────────────────────────────────
  function alertDescription(a: TradingAlert): string {
    if (a.alertCategory === 'PRICE') {
      const condLabel: Record<PriceCondition, string> = {
        ABOVE: 'Price ≥', BELOW: 'Price ≤', CROSSES_UP: 'Crosses up through', CROSSES_DOWN: 'Crosses down through',
      };
      return `${condLabel[a.condition]} ₹${a.targetPrice?.toLocaleString('en-IN')}`;
    }
    switch (a.triggerType) {
      case 'EMA_CROSS': return `EMA${a.emaFast} × EMA${a.emaSlow} crossover`;
      case 'RSI_BREACH': return `RSI(${a.rsiPeriod}) ${a.rsiDirection === 'BELOW' ? 'drops below' : 'rises above'} ${a.rsiLevel}`;
      case 'VWAP_CROSS': return 'Price crosses VWAP';
      case 'MACD_CROSS': return 'MACD signal line cross';
      case 'BOLLINGER_BREAK': return 'Bollinger Band breakout';
      default: return a.triggerType || '—';
    }
  }

  function currentLTP(ticker: string): string {
    const t = ticks[ticker];
    return t ? `₹${t.ltp.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
  }

  function ltpColor(ticker: string): string {
    const t = ticks[ticker];
    if (!t) return 'text-muted-foreground';
    return t.pct >= 0 ? 'text-positive' : 'text-negative';
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-background">
      {/* ── Toast Notifications ── */}
      <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto flex items-start gap-3 bg-card border border-warning/40 rounded-lg px-4 py-3 shadow-lg max-w-sm animate-in slide-in-from-right-4">
            <Zap size={16} className="text-warning flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground">{toast.ticker} Alert Triggered</p>
              <p className="text-xs text-muted-foreground mt-0.5">{toast.message}</p>
              <p className="text-xs text-warning font-mono mt-0.5">LTP: ₹{toast.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
        <div>
          <h1 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Bell size={20} className="text-primary" />
            Alerts
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Price levels & technical triggers — fires on live ticks
          </p>
        </div>
        <button
          onClick={() => { setShowForm(true); setError(null); }}
          className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <Plus size={15} />
          New Alert
        </button>
      </div>

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-3 gap-3 px-6 py-4 flex-shrink-0">
        <div className="bg-card border border-border rounded-lg p-3">
          <p className="text-2xs text-muted-foreground uppercase tracking-wider">Total</p>
          <p className="text-2xl font-bold text-foreground mt-1">{alerts.length}</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-3">
          <p className="text-2xs text-muted-foreground uppercase tracking-wider">Active</p>
          <p className="text-2xl font-bold text-primary mt-1">{activeCount}</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-3">
          <p className="text-2xs text-muted-foreground uppercase tracking-wider">Triggered</p>
          <p className="text-2xl font-bold text-warning mt-1">{triggeredCount}</p>
        </div>
      </div>

      {/* ── New Alert Form ── */}
      {showForm && (
        <div className="mx-6 mb-4 bg-card border border-border rounded-lg p-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-foreground">Create Alert</h2>
            <button onClick={() => { setShowForm(false); setForm(defaultForm); setError(null); }} className="text-muted-foreground hover:text-foreground">
              <X size={16} />
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-negative/10 border border-negative/20 rounded-md px-3 py-2 mb-3">
              <AlertTriangle size={14} className="text-negative flex-shrink-0" />
              <p className="text-xs text-negative">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {/* Ticker */}
            <div>
              <label className="block text-2xs font-medium text-muted-foreground mb-1">Ticker *</label>
              <input
                type="text"
                value={form.ticker}
                onChange={e => {
                  const t = e.target.value.toUpperCase();
                  setForm(f => ({ ...f, ticker: t, companyName: KNOWN_STOCKS[t] || f.companyName }));
                }}
                placeholder="e.g. RELIANCE"
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
              />
            </div>
            {/* Company */}
            <div>
              <label className="block text-2xs font-medium text-muted-foreground mb-1">Company Name</label>
              <input
                type="text"
                value={form.companyName}
                onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))}
                placeholder="Auto-filled"
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
              />
            </div>

            {/* Alert Category */}
            <div className="col-span-2">
              <label className="block text-2xs font-medium text-muted-foreground mb-1">Alert Type</label>
              <div className="flex gap-2">
                {(['PRICE', 'TECHNICAL'] as AlertCategory[]).map(cat => (
                  <button
                    key={cat}
                    onClick={() => setForm(f => ({ ...f, alertCategory: cat }))}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                      form.alertCategory === cat
                        ? 'bg-primary/10 border-primary text-primary' :'bg-background border-border text-muted-foreground hover:border-primary/50'
                    }`}
                  >
                    {cat === 'PRICE' ? <Target size={12} /> : <Activity size={12} />}
                    {cat === 'PRICE' ? 'Price Level' : 'Technical Trigger'}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Alert Fields */}
            {form.alertCategory === 'PRICE' && (
              <>
                <div>
                  <label className="block text-2xs font-medium text-muted-foreground mb-1">Condition</label>
                  <select
                    value={form.condition}
                    onChange={e => setForm(f => ({ ...f, condition: e.target.value as PriceCondition }))}
                    className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                  >
                    <option value="ABOVE">Price ≥ (Above or equal)</option>
                    <option value="BELOW">Price ≤ (Below or equal)</option>
                    <option value="CROSSES_UP">Crosses Up Through</option>
                    <option value="CROSSES_DOWN">Crosses Down Through</option>
                  </select>
                </div>
                <div>
                  <label className="block text-2xs font-medium text-muted-foreground mb-1">Target Price (₹) *</label>
                  <input
                    type="number"
                    value={form.targetPrice}
                    onChange={e => setForm(f => ({ ...f, targetPrice: e.target.value }))}
                    placeholder="e.g. 2500"
                    className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  />
                </div>
              </>
            )}

            {/* Technical Alert Fields */}
            {form.alertCategory === 'TECHNICAL' && (
              <>
                <div className="col-span-2">
                  <label className="block text-2xs font-medium text-muted-foreground mb-1">Trigger</label>
                  <div className="flex flex-wrap gap-2">
                    {(Object.keys(TRIGGER_LABELS) as TriggerType[]).map(t => (
                      <button
                        key={t}
                        onClick={() => setForm(f => ({ ...f, triggerType: t }))}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                          form.triggerType === t
                            ? 'bg-primary/10 border-primary text-primary' :'bg-background border-border text-muted-foreground hover:border-primary/50'
                        }`}
                      >
                        {TRIGGER_LABELS[t]}
                      </button>
                    ))}
                  </div>
                </div>

                {form.triggerType === 'EMA_CROSS' && (
                  <>
                    <div>
                      <label className="block text-2xs font-medium text-muted-foreground mb-1">Fast EMA Period</label>
                      <input type="number" value={form.emaFast} onChange={e => setForm(f => ({ ...f, emaFast: e.target.value }))}
                        className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary" />
                    </div>
                    <div>
                      <label className="block text-2xs font-medium text-muted-foreground mb-1">Slow EMA Period</label>
                      <input type="number" value={form.emaSlow} onChange={e => setForm(f => ({ ...f, emaSlow: e.target.value }))}
                        className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary" />
                    </div>
                  </>
                )}

                {form.triggerType === 'RSI_BREACH' && (
                  <>
                    <div>
                      <label className="block text-2xs font-medium text-muted-foreground mb-1">RSI Period</label>
                      <input type="number" value={form.rsiPeriod} onChange={e => setForm(f => ({ ...f, rsiPeriod: e.target.value }))}
                        className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary" />
                    </div>
                    <div>
                      <label className="block text-2xs font-medium text-muted-foreground mb-1">RSI Level</label>
                      <input type="number" value={form.rsiLevel} onChange={e => setForm(f => ({ ...f, rsiLevel: e.target.value }))}
                        placeholder="e.g. 30 or 70"
                        className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-2xs font-medium text-muted-foreground mb-1">Breach Direction</label>
                      <div className="flex gap-2">
                        {(['BELOW', 'ABOVE'] as RsiDirection[]).map(d => (
                          <button key={d} onClick={() => setForm(f => ({ ...f, rsiDirection: d }))}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                              form.rsiDirection === d ? 'bg-primary/10 border-primary text-primary' : 'bg-background border-border text-muted-foreground hover:border-primary/50'
                            }`}>
                            {d === 'BELOW' ? '↓ Falls Below (Oversold)' : '↑ Rises Above (Overbought)'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Notes */}
            <div className="col-span-2">
              <label className="block text-2xs font-medium text-muted-foreground mb-1">Notes</label>
              <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional note"
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary" />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setShowForm(false); setForm(defaultForm); setError(null); }}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-md transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center gap-2">
              {saving && <RefreshCw size={13} className="animate-spin" />}
              {saving ? 'Saving…' : 'Create Alert'}
            </button>
          </div>
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="flex gap-1 px-6 mb-3 flex-shrink-0">
        {([['active', 'Active'], ['triggered', 'Triggered'], ['all', 'All']] as const).map(([tab, label]) => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === tab ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}>
            {label}
            {tab === 'active' && activeCount > 0 && (
              <span className="ml-1.5 bg-primary/20 text-primary text-2xs px-1.5 py-0.5 rounded-full">{activeCount}</span>
            )}
            {tab === 'triggered' && triggeredCount > 0 && (
              <span className="ml-1.5 bg-warning/20 text-warning text-2xs px-1.5 py-0.5 rounded-full">{triggeredCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Alert List ── */}
      <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-2">
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-card border border-border rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filteredAlerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Bell size={40} className="text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">No alerts {activeTab !== 'all' ? `in "${activeTab}"` : ''}</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Create an alert to get notified on live ticks</p>
          </div>
        ) : (
          filteredAlerts.map(alert => (
            <AlertRow
              key={alert.id}
              alert={alert}
              ltp={currentLTP(alert.ticker)}
              ltpColor={ltpColor(alert.ticker)}
              description={alertDescription(alert)}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Alert Row ────────────────────────────────────────────────────────────────

interface AlertRowProps {
  alert: TradingAlert;
  ltp: string;
  ltpColor: string;
  description: string;
  onDelete: (id: string) => void;
  onToggle: (alert: TradingAlert) => void;
}

function AlertRow({ alert, ltp, ltpColor, description, onDelete, onToggle }: AlertRowProps) {
  const [expanded, setExpanded] = useState(false);

  const categoryIcon = alert.alertCategory === 'PRICE'
    ? <Target size={13} className="text-primary" />
    : <Activity size={13} className="text-accent" />;

  const statusBadge = alert.isTriggered
    ? <span className="flex items-center gap-1 text-2xs font-semibold text-warning bg-warning/10 px-2 py-0.5 rounded-full"><CheckCircle2 size={10} />Triggered</span>
    : alert.isActive
      ? <span className="flex items-center gap-1 text-2xs font-semibold text-positive bg-positive/10 px-2 py-0.5 rounded-full"><Bell size={10} />Active</span>
      : <span className="flex items-center gap-1 text-2xs font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-full"><BellOff size={10} />Paused</span>;

  return (
    <div className={`bg-card border rounded-lg transition-all ${alert.isTriggered ? 'border-warning/30' : alert.isActive ? 'border-border' : 'border-border/50 opacity-70'}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Category icon */}
        <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
          {categoryIcon}
        </div>

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground">{alert.ticker}</span>
            {statusBadge}
            <span className="text-2xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              {alert.alertCategory === 'PRICE' ? 'Price' : (alert.triggerType ? alert.triggerType.replace('_', ' ') : 'Technical')}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{description}</p>
        </div>

        {/* LTP */}
        <div className="text-right flex-shrink-0">
          <p className={`text-sm font-mono font-semibold ${ltpColor}`}>{ltp}</p>
          <p className="text-2xs text-muted-foreground">LTP</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {!alert.isTriggered && (
            <button onClick={() => onToggle(alert)} title={alert.isActive ? 'Pause' : 'Resume'}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              {alert.isActive ? <BellOff size={14} /> : <Bell size={14} />}
            </button>
          )}
          <button onClick={() => onDelete(alert.id)} title="Delete"
            className="p-1.5 rounded-md text-muted-foreground hover:text-negative hover:bg-negative/10 transition-colors">
            <Trash2 size={14} />
          </button>
          <button onClick={() => setExpanded(e => !e)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-3 pt-0 border-t border-border/50 mt-1">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mt-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Company</span>
              <span className="text-foreground font-medium">{alert.companyName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created</span>
              <span className="text-foreground">{new Date(alert.createdAt).toLocaleDateString('en-IN')}</span>
            </div>
            {alert.isTriggered && alert.triggeredAt && (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Triggered At</span>
                  <span className="text-warning font-medium">{new Date(alert.triggeredAt).toLocaleTimeString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Triggered Price</span>
                  <span className="text-warning font-mono">₹{alert.triggeredPrice?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              </>
            )}
            {alert.notes && (
              <div className="col-span-2 flex justify-between">
                <span className="text-muted-foreground">Notes</span>
                <span className="text-foreground">{alert.notes}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
